// utils/logger.ts

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
}

export interface LogEntry {
  timestamp: Date;
  level: LogLevel;
  message: string;
  data?: any;
}

export interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  persist: boolean;
  maxPersistedLogs: number;
}

class Logger {
  private config: LoggerConfig;
  private logs: LogEntry[] = [];
  private readonly levelLabels = ["ERROR", "WARN", "INFO", "DEBUG"];
  private readonly levelColors = ["red", "orange", "green", "blue"];

  constructor() {
    this.config = {
      enabled: process.env.REACT_APP_LOG_ENABLED !== "false",
      level: this.parseLogLevel(process.env.REACT_APP_LOG_LEVEL || "debug"),
      persist: process.env.REACT_APP_LOG_PERSIST === "true",
      maxPersistedLogs: parseInt(
        process.env.REACT_APP_LOG_MAX_ENTRIES || "1000",
        10
      ),
    };
  }

  private parseLogLevel(level: string): LogLevel {
    switch (level.toLowerCase()) {
      case "error":
        return LogLevel.ERROR;
      case "warn":
        return LogLevel.WARN;
      case "info":
        return LogLevel.INFO;
      case "debug":
        return LogLevel.DEBUG;
      default:
        return LogLevel.DEBUG;
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return this.config.enabled && level <= this.config.level;
  }

  private addToPersistentLogs(
    level: LogLevel,
    message: string,
    data?: any
  ): void {
    if (this.config.persist) {
      this.logs.push({
        timestamp: new Date(),
        level,
        message,
        data,
      });

      // Trim logs if exceeding maximum
      if (this.logs.length > this.config.maxPersistedLogs) {
        this.logs = this.logs.slice(-this.config.maxPersistedLogs);
      }
    }
  }

  private formatMessage(level: LogLevel, message: string): string {
    return `%c[${new Date().toISOString()}] [${
      this.levelLabels[level]
    }] ${message}`;
  }

  private getStyle(level: LogLevel): string {
    return `color: ${this.levelColors[level]}; font-weight: bold;`;
  }

  debug(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.debug(
        this.formatMessage(LogLevel.DEBUG, message),
        this.getStyle(LogLevel.DEBUG),
        data || ""
      );
      this.addToPersistentLogs(LogLevel.DEBUG, message, data);
    }
  }

  info(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(
        this.formatMessage(LogLevel.INFO, message),
        this.getStyle(LogLevel.INFO),
        data || ""
      );
      this.addToPersistentLogs(LogLevel.INFO, message, data);
    }
  }

  warn(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(
        this.formatMessage(LogLevel.WARN, message),
        this.getStyle(LogLevel.WARN),
        data || ""
      );
      this.addToPersistentLogs(LogLevel.WARN, message, data);
    }
  }

  error(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(
        this.formatMessage(LogLevel.ERROR, message),
        this.getStyle(LogLevel.ERROR),
        data || ""
      );
      this.addToPersistentLogs(LogLevel.ERROR, message, data);
    }
  }

  // Configuration methods
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }

  setLevel(level: LogLevel): void {
    this.config.level = level;
  }

  setPersist(persist: boolean): void {
    this.config.persist = persist;
  }

  // Get logs for debugging purposes
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clearLogs(): void {
    this.logs = [];
  }

  // Get current configuration
  getConfig(): LoggerConfig {
    return { ...this.config };
  }
}

// Create a singleton instance
export const logger = new Logger();
export default logger;
